import React from 'react';
import moment from 'moment';
import { CreditApplication, InternalFormSubmission } from '@dill/dill-shared';
import { Eye } from 'react-feather';
import { DotsThreeVertical, DownloadSimple, PaperPlaneTilt, Trash } from '@phosphor-icons/react';
import { DillButton, DillDropdownButton } from '../../../general/Dill';
import { COLORS } from '../../../../utils/colors';
import { InternalFormStatusBadge } from './InternalFormSubmissions';
import { getFormTypeLabel } from './internalFormUtils';

type InternalFormSubmissionRow = Omit<InternalFormSubmission, 'accessToken'>;

// Internal form submissions are listed in the Credit Applications table alongside the
// applications. They're carried as CreditApplication-shaped rows (so the page's existing filters —
// application type, status, branch — and the table's sorting work on them) with the submission
// itself attached; the table renders those rows with the cells below instead of its own.
export type WithInternalFormSubmission = {
  internalFormSubmission?: InternalFormSubmissionRow;
};

export const isInternalFormRow = (row: object): boolean =>
  !!(row as WithInternalFormSubmission).internalFormSubmission;

export const toInternalFormTableRow = <T extends CreditApplication>(
  submission: InternalFormSubmissionRow
): T & WithInternalFormSubmission =>
  ({
    id: submission.id,
    supplierId: submission.supplierId,
    templateId: submission.templateId,
    // Not a real CreditApplication applicationType — lets the application type filter match it.
    applicationType: submission.formType === 'EXTERNAL' ? 'EXTERNAL_FORM' : 'INTERNAL_FORM',
    status: submission.status,
    createdAt: submission.createdAt,
    branchId: submission.branchId ?? undefined,
    requestedBy: submission.sentByName,
    senderEmail: submission.sentByEmail,
    isArchived: false,
    fieldValues: [],
    invitedSignatures: [],
    // Read by the table's Notes column, which works the same for internal forms.
    internalNotes: submission.internalNotes ?? [],
    applicationUpdateHistoryId: null,
    internalFormSubmission: submission,
  } as unknown as T & WithInternalFormSubmission);

const Muted = () => <p className="text-xs GREY_400-CLR">—</p>;

// Field ids an internal form keeps the customer's name in, most specific first — the templates use
// customer-or-company-name, same as credit applications.
const COMPANY_NAME_FIELD_IDS = ['customer-or-company-name', 'customer-name'];

// The customer's name as answered on the form (top level, or nested in a conditional field),
// or '' when the form doesn't have one or it wasn't filled in yet.
export const getInternalFormCompanyName = (submission: InternalFormSubmissionRow): string => {
  const answers = submission.fieldValues.flatMap((field) => [
    field,
    ...(field.fieldValue ?? []).filter((item: any) => !item.hidden),
  ]);
  for (const id of COMPANY_NAME_FIELD_IDS) {
    const value = answers.find((field) => field.id === id)?.value;
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
};

// Per column key; columns not listed here render nothing for an internal form row.
export const getInternalFormCell = ({
  columnKey,
  submission,
  branchName,
  onReview,
  onPdf,
  onResend,
  onDelete,
  isPdfLoading,
  isPdfDisabled,
}: {
  columnKey: string;
  submission: InternalFormSubmissionRow;
  branchName: string;
  onReview: () => void;
  // Preview / Download, once the form has been filled in — same menu as a credit application's.
  onPdf: (type: 'preview' | 'download') => void;
  // Resend, while the form hasn't been filled in yet.
  onResend: () => void;
  // Omitted when the user can't delete (sales role).
  onDelete?: () => void;
  isPdfLoading: boolean;
  isPdfDisabled: boolean;
}): React.ReactNode => {
  switch (columnKey) {
    case 'contact':
      return (
        <div className="flex flex-col w-full min-w-0">
          <p className="text-xs w-full truncate">
            {submission.sentByName || submission.sentByEmail}
          </p>
          {submission.recipientName && (
            <p className="text-xs w-full truncate">{submission.recipientName}</p>
          )}
          <p className="text-xs GREY_500-CLR w-full truncate">
            {submission.signature?.signedByEmail ?? submission.recipientEmails.join(', ')}
          </p>
        </div>
      );
    case 'company': {
      // The customer's name when the form has one, with the form name under it; otherwise the
      // form name alone.
      const companyName = getInternalFormCompanyName(submission);
      return (
        <div className="flex flex-col">
          <p className="text-xs font-medium">{companyName || submission.templateName}</p>
          {companyName && <p className="text-xs GREY_500-CLR">{submission.templateName}</p>}
          {submission.billToCustomerId && (
            <p className="text-xs GREY_500-CLR">{`Bill-To #${submission.billToCustomerId}`}</p>
          )}
        </div>
      );
    }
    case 'createdAt':
      return (
        <div className="flex flex-col">
          <p className="text-xs">{moment(submission.createdAt).format('MM/DD/YYYY')}</p>
          {submission.submittedAt && (
            <p className="text-xs PRIMARY_500-CLR">
              {`Submitted: ${moment(submission.submittedAt).format('MM/DD/YYYY')}`}
            </p>
          )}
        </div>
      );
    case 'status':
      return (
        <div className="flex flex-col items-start gap-1">
          <InternalFormStatusBadge status={submission.status} />
          <p className="text-[11px] GREY_500-CLR">{getFormTypeLabel(submission)}</p>
        </div>
      );
    case 'branch':
      return branchName ? <p className="text-xs">{branchName}</p> : <Muted />;
    case 'approvedAt':
      return submission.status === 'APPROVED' && submission.decidedAt ? (
        <p className="text-xs">{moment(submission.decidedAt).format('MM/DD/YYYY')}</p>
      ) : (
        <Muted />
      );
    case 'action': {
      const canResend = submission.status === 'SENT';
      const hasPdf = submission.status !== 'SENT';
      return (
        <div className="flex items-center justify-center gap-1.5 w-full">
          {/* Reserves the Resend button's slot so Review lines up with credit application rows. */}
          <div className="w-[34px] shrink-0" />
          <DillButton label="Review" onClick={onReview} width={100} />
          {(canResend || hasPdf || onDelete) && (
            <DillDropdownButton
              variant="icon-default"
              options={[
                ...(canResend
                  ? [
                      {
                        label: 'Resend',
                        value: 'resend',
                        icon: <PaperPlaneTilt color={COLORS.GREY_500} size={20} />,
                      },
                    ]
                  : []),
                ...(hasPdf
                  ? [
                      {
                        label: 'Preview',
                        value: 'preview',
                        icon: <Eye color={COLORS.GREY_500} size={20} />,
                      },
                      {
                        label: 'Download',
                        value: 'download',
                        icon: <DownloadSimple color={COLORS.GREY_500} size={20} />,
                      },
                    ]
                  : []),
                ...(onDelete
                  ? [
                      {
                        label: 'Delete',
                        value: 'delete',
                        icon: <Trash color={COLORS.ERROR_500} size={20} />,
                      },
                    ]
                  : []),
              ]}
              onSelect={(option) => {
                if (option.value === 'resend') onResend();
                else if (option.value === 'delete') onDelete?.();
                else onPdf(option.value as 'preview' | 'download');
              }}
              isDisabled={isPdfDisabled}
              isLoading={isPdfLoading}
              icon={<DotsThreeVertical size={18} />}
              height={34}
              width={34}
            />
          )}
        </div>
      );
    }
    case 'address':
    case 'amount':
    case 'ballInCourt':
      return <Muted />;
    default:
      return null;
  }
};

export const getInternalFormSortValue = (
  columnKey: string,
  submission: InternalFormSubmissionRow
): string | number => {
  switch (columnKey) {
    case 'contact':
      return (submission.sentByName || submission.sentByEmail).toLowerCase();
    case 'company':
      return (getInternalFormCompanyName(submission) || submission.templateName).toLowerCase();
    case 'createdAt':
      return submission.createdAt ? new Date(submission.createdAt).getTime() : 0;
    case 'approvedAt':
      return submission.decidedAt && submission.status === 'APPROVED'
        ? new Date(submission.decidedAt).getTime()
        : 0;
    case 'status':
      return submission.status.toLowerCase();
    default:
      return '';
  }
};

export const getInternalFormSearchValue = (
  columnKey: string,
  submission: InternalFormSubmissionRow
): string => {
  switch (columnKey) {
    case 'contact':
      return [
        submission.sentByName,
        submission.sentByEmail,
        ...submission.recipientEmails,
        submission.recipientName ?? '',
        submission.signature?.signedByEmail ?? '',
      ].join(' ');
    case 'company':
      return `${getInternalFormCompanyName(submission)} ${submission.templateName} ${
        submission.billToCustomerId ?? ''
      } ${getFormTypeLabel(submission).toLowerCase()}`;
    case 'createdAt':
      return submission.createdAt ? moment(submission.createdAt).format('MM/DD/YYYY') : '';
    case 'status':
      return `${submission.status.toLowerCase()} ${getFormTypeLabel(submission).toLowerCase()}`;
    default:
      return '';
  }
};
